/**
 * In-App Agent API service
 *
 * Handles communication with the backend in-app agent for FE tool execution
 * and contextual AI assistance within the application.
 */

import api from "@/services/api/client";

export interface InAppAgentMessage {
  role: "user" | "assistant";
  content: string;
}

export interface OsContextWindow {
  appId: string;
  title: string;
  isFocused: boolean;
  isMinimized: boolean;
  params?: Record<string, string>;
}

export interface OsContextWorkspace {
  id: string;
  name: string;
  isActive: boolean;
  windows: OsContextWindow[];
}

export interface OsContextData {
  workspaces: OsContextWorkspace[];
  focusedApp: string;
  focusedWindow: {
    appId: string;
    title: string;
    params?: Record<string, string>;
  } | null;
}

export interface FeContext {
  current_app: string;
  app_state?: Record<string, unknown>;
  // OS layout context
  os_context?: OsContextData;
  // Personality mode for Ani's tone
  personality_mode?: "professional" | "casual" | "fun";
  /** Runtime demo context assembled on the client (brief, task queue). */
  agent_context?: Record<string, unknown>;
  // Legacy fields
  route?: string;
  api_context?: {
    recent_calls?: Array<{
      method: string;
      endpoint: string;
      timestamp: number;
    }>;
    active_resources?: string[];
  };
}

/**
 * Attachment sent with an in-app agent message. Mirrors the chat attachment shape
 * (`ChatMessageAttachment`) so attachments collected in the UI can be forwarded
 * without conversion. `document` is the legacy kind; entity/conversation are current.
 */
export interface MessageAttachment {
  type: "document" | "entity_document" | "conversation_document";
  document_id: string;
  display_name?: string;
  mime_type?: string;
  size?: number;
}

export type { StartTurnPayload, TurnEventEnvelope } from "@/services/inAppAgent/turnStream";
export {
  startTurn,
  subscribeTurnEvents,
  submitFeToolResults,
  cancelTurn,
} from "@/services/inAppAgent/turnStream";

export interface FeToolCall {
  tool_id: string;
  params: Record<string, unknown>;
  instructions?: string;
}

export interface FeToolResult {
  tool_id: string;
  success: boolean;
  result?: Record<string, unknown>;
  error?: string;
}

export interface InAppAgentToolStep {
  tool_name: string;
  tool_label?: string;
  status?: string;
  summary?: string;
  mcp_connector_id?: string;
}

export interface McpToolDebugInfo {
  tool_id: string;
  display_name: string;
  mcp_tool_name: string;
  allowlisted: boolean;
  discovered: boolean;
}

export interface McpServerDebugInfo {
  connector_id: string;
  url: string;
  enabled: boolean;
  configured: boolean;
  connected: boolean;
  missing_config_fields?: string[];
  configuration?: Record<string, string>;
  discovery_error?: string | null;
  tools: McpToolDebugInfo[];
}

export interface McpCatalogResponse {
  company_id: string;
  servers: McpServerDebugInfo[];
}

export interface InAppAgentProfileResponse {
  company_id: string;
  profile: Record<string, unknown> | null;
}

export async function getInAppAgentProfile(
  profileCompanyId?: string,
): Promise<InAppAgentProfileResponse> {
  const trimmed = profileCompanyId?.trim();
  const response = await api.get<InAppAgentProfileResponse>("/agentic/in-app-agent/profile", {
    params: trimmed ? { profile_company_id: trimmed } : undefined,
  });
  return response.data;
}

export async function getMcpCatalog(): Promise<McpCatalogResponse> {
  const response = await api.get<McpCatalogResponse>("/agentic/in-app-agent/mcp-catalog");
  return response.data;
}

export interface InAppAgentChatMessage {
  _id: string;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
  actions?: Array<{
    type: string;
    label: string;
    url?: string;
    [key: string]: unknown;
  }>;
  attachments?: Array<{
    type: "document";
    document_id: string;
    display_name?: string;
    mime_type?: string;
    size?: number;
  }>;
  sources?: Array<{
    document_name: string;
    document_id: string;
    document_date?: string;
    justification: string;
  }>;
  sender?: {
    id: string;
    name: string;
    type: string;
  };
  metadata?: {
    embeds?: unknown[];
    fe_tool_calls?: unknown;
    continue_after_tools?: boolean;
    tool_step?: InAppAgentToolStep;
    timeline?: unknown[];
  };
}

/**
 * Get chat messages for a frontend session.
 *
 * @param feSessionId - The frontend session ID
 * @returns Array of chat messages
 */
export async function getInAppAgentMessages(
  feSessionId: string
): Promise<InAppAgentChatMessage[]> {
  const response = await api.get<InAppAgentChatMessage[]>(
    `/agentic/in-app-agent/messages/${feSessionId}`
  );

  return response.data;
}

export interface InAppAgentSession {
  session_id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  last_message_at?: string;
  message_count: number;
}

/**
 * List all sessions for the current user.
 *
 * @param limit - Maximum number of sessions to return (default 50)
 * @returns Array of sessions sorted by last_message_at descending
 */
export async function listInAppAgentSessions(
  limit: number = 50
): Promise<InAppAgentSession[]> {
  const response = await api.get<InAppAgentSession[]>(
    `/agentic/in-app-agent/sessions`,
    { params: { limit } }
  );

  return response.data;
}

/**
 * Create a new session.
 *
 * @param title - Optional title for the session
 * @param sessionId - Optional session ID (if not provided, backend generates one)
 * @returns The created session
 */
export async function createInAppAgentSession(
  title?: string,
  sessionId?: string
): Promise<InAppAgentSession> {
  const response = await api.post<InAppAgentSession>(
    `/agentic/in-app-agent/sessions`,
    { title, session_id: sessionId }
  );

  return response.data;
}

/**
 * Delete a session and its messages.
 *
 * @param sessionId - The session ID to delete
 */
export async function deleteInAppAgentSession(
  sessionId: string
): Promise<void> {
  await api.delete(`/agentic/in-app-agent/sessions/${sessionId}`);
}
